import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { ITodolistTemplate } from '~/types/todolist-template'

import Button from '~/components/Buttons/Button'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import {
  useCreateTodolistTemplateMutation,
  keys as todolistTemplatesKeys,
  useUpdateTodolistTemplateMutation,
} from '~/queries/todolist-templates'
import { displayToast } from '~/utils/toast'

export interface FormValues {
  description: string
  tasks: {
    description: string
    group_names: string[]
    previous_steps: string[]
    title: string
  }[]
  title: string
}

const validationSchema = Yup.object({
  description: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  tasks: Yup.array()
    .of(
      Yup.object({
        description: Yup.string().required(FORM_ERRORS.REQUIRED),
        group_names: Yup.array().of(Yup.string().required()).required(),
        previous_steps: Yup.array().of(Yup.string().required()).required(),
        title: Yup.string().required(FORM_ERRORS.REQUIRED),
      })
    )
    .required(),
  title: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  onClose: VoidFunction
  onSuccess: (templateId: string) => void
  todolistTemplate?: ITodolistTemplate
}

const Form = ({ onClose, onSuccess, todolistTemplate }: FormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<FormValues>({
    defaultValues: {
      description: todolistTemplate?.template.description || '',
      tasks: todolistTemplate?.tasks || [],
      title: todolistTemplate?.template.title || '',
    },
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { mutateAsync: createTodolistTemplate } = useCreateTodolistTemplateMutation()

  const { mutateAsync: updateTodolistTemplate } = useUpdateTodolistTemplateMutation()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    try {
      const res = todolistTemplate
        ? await updateTodolistTemplate({
            data: {
              tasks: values.tasks,
              template: {
                ...todolistTemplate.template,
                description: values.description.trim(),
                title: values.title.trim(),
              },
            },
            templateId: todolistTemplate.template.template_id,
          })
        : await createTodolistTemplate({
            tasks: values.tasks,
            template: {
              description: values.description.trim(),
              title: values.title.trim(),
            },
          })

      await queryClient.invalidateQueries({ queryKey: todolistTemplatesKeys.list() })

      if (todolistTemplate) {
        await queryClient.invalidateQueries({
          queryKey: todolistTemplatesKeys.details(todolistTemplate.template.template_id),
        })
      }

      displayToast(todolistTemplate ? 'Template updated successfully' : 'Template created successfully', 'success')
      onSuccess(res.template.template_id)
    } catch (e) {
      captureException(e)
      displayToast('Template cannot be created!', 'error')
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody className="set-todolist-template-modal__body">
        <RequiredInformation />
        <TextInput
          {...register('title')}
          error={errors.title?.message}
          label="Title"
          placeholder="Type a title"
          required
        />
        <TextInput
          {...register('description')}
          error={errors.description?.message}
          label="Description"
          placeholder="Type a description"
          required
        />
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          Save
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const SetTodolistTemplateModal = ({ isOpen, onClose, onSuccess, todolistTemplate }: ModalProps): ReactElement => {
  return (
    <Modal className="set-todolist-template-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{todolistTemplate ? 'Update' : 'Create'} a template</ModalHeader>
      <Form onClose={onClose} onSuccess={onSuccess} todolistTemplate={todolistTemplate} />
    </Modal>
  )
}

export default SetTodolistTemplateModal
